import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setHeroContent, setHeroImage, setHeroTitle } from '../../redux/hero.slice';
import laptopMockup from '../../assets/laptop-mockup.png';
import { PAGE_METADATA } from '../../pageMetadata';
import PageMetadata from '../../shared/PageMetadata';
import RelatedPulseLinks from '../../shared/RelatedPulseLinks';
import './AboutPage.css';

const COMPANY_HOUSE_URL = 'https://find-and-update.company-information.service.gov.uk/company/13745799';
const CANONICAL_HREF = 'https://www.shuttleops.com/about';

const organizationData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.shuttleops.com/#organization',
  name: 'Westwood Artificial Intelligence Ltd',
  legalName: 'Westwood Artificial Intelligence Ltd',
  alternateName: 'ShuttleOps',
  url: 'https://www.shuttleops.com/',
  foundingDate: '2021-11-16',
  founder: {
    '@type': 'Person',
    name: 'Andy Feltham',
  },
  brand: {
    '@type': 'Brand',
    name: 'ShuttleOps',
  },
};

const AboutPage = () => {
  const dispatch = useDispatch();
  const metadata = PAGE_METADATA['/about'];

  useEffect(() => {
    dispatch(setHeroImage(false));
    dispatch(setHeroTitle('Built from real badminton club nights'));
    dispatch(
      setHeroContent(
        'ShuttleOps helps club teams run sessions, payments, communication, and fair court rotation in one place.'
      )
    );
  }, [dispatch]);

  useEffect(() => {
    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', CANONICAL_HREF);
    document.head.appendChild(canonical);

    return () => canonical.remove();
  }, []);

  return (
    <main className="about-page">
      <PageMetadata metadata={metadata} />
      <script type="application/ld+json">{JSON.stringify(organizationData)}</script>

      <section className="about-story" aria-labelledby="about-origin-title">
        <div className="about-story__copy">
          <span className="about-kicker">Why ShuttleOps exists</span>
          <h2 id="about-origin-title">Empty courts and unfair games started the idea.</h2>
          <p>
            Andy Feltham plays badminton and helps run club sessions. He saw court space go unused
            while players waited too long or met the same opponents again.
          </p>
          <p>
            As an AI developer, Andy knew software could make those choices clearer. He built
            ShuttleOps around the whole club night, not only the court rotation.
          </p>
        </div>
        <figure className="about-story__visual">
          <img src={laptopMockup} alt="The ShuttleOps Digital Peg Board running on a laptop" />
          <figcaption>Bookings, attendance, payments, and fair games share one club record.</figcaption>
        </figure>
      </section>

      <section className="about-audience" aria-labelledby="about-audience-title">
        <div className="about-audience__heading">
          <span className="about-kicker">One club, two useful views</span>
          <h2 id="about-audience-title">Less admin for organisers. A simpler night for players.</h2>
        </div>
        <div className="about-audience__cards">
          <article>
            <h3>For club teams</h3>
            <p>
              Manage members, sessions, attendance, payments, messages, and reports without
              joining several tools together.
            </p>
          </article>
          <article>
            <h3>For players</h3>
            <p>
              Book sessions, pay, receive updates, review activity, and get more varied court time
              through the Digital Peg Board.
            </p>
          </article>
        </div>
      </section>

      <section className="about-company" aria-labelledby="about-company-title">
        <div>
          <span className="about-kicker">The company behind the product</span>
          <h2 id="about-company-title">Built close to the problem.</h2>
          <p>
            Westwood Artificial Intelligence Ltd builds ShuttleOps. Andy Feltham is the founder,
            an AI developer, a badminton player, and the company's active director.
          </p>
          <p>
            Andy marks 18 September 2024 as the start of ShuttleOps. The team keeps shipping
            frequent updates across the web, iPhone, iPad, and Android apps.
          </p>
        </div>
        <dl className="about-facts">
          <div>
            <dt>Legal company</dt>
            <dd>Westwood Artificial Intelligence Ltd</dd>
          </div>
          <div>
            <dt>Companies House</dt>
            <dd>
              <a href={COMPANY_HOUSE_URL}>Company 13745799</a>
            </dd>
          </div>
          <div>
            <dt>Incorporated</dt>
            <dd>16 November 2021</dd>
          </div>
          <div>
            <dt>ShuttleOps start</dt>
            <dd>18 September 2024, founder-confirmed</dd>
          </div>
        </dl>
      </section>

      <RelatedPulseLinks
        articles={[]}
        label="See ShuttleOps in action"
        links={[
          { title: 'Explore tools for club managers', path: '/features/managers' },
          { title: 'See the Digital Peg Board', path: '/pegboard' },
          { title: 'Compare plans and member allowances', path: '/pricing' },
        ]}
      />
    </main>
  );
};

export default AboutPage;
